/**
 * `/atlas` — the learner's own sky.
 *
 * Everything on this page comes from `GET /api/coaching/atlas`: the
 * rings and their order from the registry taxonomy, the planets from the
 * planet rows placed on them, the claims from each planet's roster, the
 * instrument shelves from the published catalog, and every reading —
 * rung, gap, grip, credit — from the learner's own folded log.  There is
 * no seeded, sampled or simulated state anywhere in this tree.
 *
 * The layout is phone-first: one column, a full-bleed map, tap targets
 * at 44px, controls folded into a disclosure so the sky is above the
 * fold on a small screen, and the opened claim's shelf scrolled to
 * rather than left below the map.
 *
 * The map is drawn one way — the altitudes seating (rings = how deep,
 * wedges = which family), the weave, a solid track, no bands, no marks
 * (`atlas/settings.ts`).  The two readings a learner still chooses are
 * the guide and the links; the credit style stays because borrowed
 * ground is the one thing the sky must not draw ambiguously.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Orrery } from "./atlas/Orrery";
import { CREDIT_STYLES, type CreditStyle } from "./atlas/Mastery";
import { ClaimShelf, PlanetCard, RingRoster, SectorCard, SkyCard, TrackCard } from "./atlas/cards";
import {
  DARK, LIGHT, MONO, SERIF, type Palette, gaugeColours, ringColour, sectorColour, trackColour,
} from "./atlas/palette";
import { arcPath } from "./atlas/geometry";
import { hash32 } from "./atlas/planetLook";
import {
  DEFAULT_GUIDE, DEFAULT_LINKS, GUIDES, LINKS, type Guide, type Links, unlitFor,
} from "./atlas/settings";
import { useAtlas, useNarrow, useNightSky } from "./atlas/useAtlas";
import type { InstrumentReading } from "./atlas/types";

/**
 * The browser's focus ring, suppressed across the whole screen.
 *
 * It is drawn in the user agent's own idiom — a square dotted box, or a
 * blue halo — around whatever was last tapped: a world, a gate, a family
 * name, a chip.  The atlas already says what is chosen, in its own
 * language (a world swells and lights, a chip fills, a gate brightens),
 * and the ring lands on top of that and reads as a rendering fault.
 *
 * `-webkit-tap-highlight-color` goes with it: on a phone the same tap
 * flashes a grey block over the element, which is the same intrusion by
 * another name.
 */
const NO_FOCUS_RING =
  '[data-testid="atlas-page"] :focus,' +
  '[data-testid="atlas-page"] :focus-visible{outline:none}' +
  '[data-testid="atlas-page"]{-webkit-tap-highlight-color:transparent}';

function Chip({
  active, colour, P, onClick, children,
}: {
  active: boolean;
  colour: string;
  P: Palette;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        borderRadius: 999,
        padding: "7px 12px",
        minHeight: 36,
        fontFamily: MONO,
        fontSize: 12,
        cursor: "pointer",
        border: `1px solid ${active ? colour : P.line}`,
        color: active ? colour : P.dim,
        background: active ? P.panel2 : "transparent",
      }}
    >
      {children}
    </button>
  );
}

/**
 * A 15px preview of what a credit style does to a gauge.
 *
 * The three modes are hard to choose between from their names alone —
 * the reference field drew each chip's own arcs for exactly that reason,
 * and a learner picking how borrowed ground should look wants to see it
 * rather than read about it.
 */
function CreditSwatch({ style, P }: { style: CreditStyle; P: Palette }) {
  const [inner, outer] = gaugeColours(P);
  return (
    <svg width="15" height="15" viewBox="-9 -9 18 18" style={{ display: "block" }} aria-hidden>
      <circle r="2.4" fill={P.dim} fillOpacity="0.4" />
      {style === "hollow" ? (
        <>
          {[4.4, 5.4].map((r) => (
            <path key={r} d={arcPath(0, 0, r, -90, 210)} fill="none" stroke={inner} strokeWidth="0.5" strokeLinecap="round" />
          ))}
          {[7.4, 8.2].map((r) => (
            <path key={r} d={arcPath(0, 0, r, -90, 40)} fill="none" stroke={outer} strokeWidth="0.5" strokeLinecap="round" />
          ))}
        </>
      ) : style === "hatched" ? (
        <>
          <path d={arcPath(0, 0, 4.9, -90, 210)} fill="none" stroke={inner} strokeWidth="1.6" strokeDasharray="0.9 0.8" />
          <path d={arcPath(0, 0, 7.8, -90, 40)} fill="none" stroke={outer} strokeWidth="1.6" strokeDasharray="0.9 0.8" />
        </>
      ) : (
        <>
          <path d={arcPath(0, 0, 4.9, -90, 60)} fill="none" stroke={inner} strokeWidth="1.6" strokeLinecap="round" />
          <path d={arcPath(0, 0, 4.9, 60, 210)} fill="none" stroke={inner} strokeWidth="1.1" strokeDasharray="0.9 1.1" />
          <path d={arcPath(0, 0, 7.8, -90, -20)} fill="none" stroke={outer} strokeWidth="1.6" strokeLinecap="round" />
          <path d={arcPath(0, 0, 7.8, -20, 40)} fill="none" stroke={outer} strokeWidth="1.1" strokeDasharray="0.9 1.1" />
        </>
      )}
    </svg>
  );
}

export function AtlasPage() {
  const { status, data, error, reload } = useAtlas();
  const navigate = useNavigate();
  const narrow = useNarrow();
  const { night, toggle } = useNightSky();
  const P: Palette = night ? DARK : LIGHT;
  const [params, setParams] = useSearchParams();

  const [ringSel, setRingSel] = useState<string | null>(null);
  const [sectorSel, setSectorSel] = useState<string | null>(null);
  const [focus, setFocus] = useState<string | null>(params.get("planet"));

  // A ``?planet=`` naming nothing must not open a world.  The camera
  // reads the id, not the reading, so an unknown one zooms the sky onto
  // an empty coordinate and hides every way back — a stale link, a
  // renamed planet, or a typo would strand the reader.
  useEffect(() => {
    if (focus && data && !data.planets.some((planet) => planet.id === focus)) setFocus(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, data]);
  const [selClaim, setSelClaim] = useState<number | null>(null);
  const [credit, setCredit] = useState<CreditStyle>("hollow");
  const [links, setLinks] = useState<Links>(DEFAULT_LINKS);
  const [guide, setGuide] = useState<Guide>(DEFAULT_GUIDE);
  const [track, setTrack] = useState<string | null>(null);
  const [controlsOpen, setControlsOpen] = useState(false);
  const shelfRef = useRef<HTMLDivElement | null>(null);

  // The open planet lives in the URL so a learner can link to a world
  // and come back to it — the one piece of view state worth persisting.
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (focus) next.set("planet", focus);
    else next.delete("planet");
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);

  const planets = data?.planets ?? [];
  const byId = useMemo(() => new Map(planets.map((planet) => [planet.id, planet])), [planets]);
  const focused = focus ? byId.get(focus) ?? null : null;
  const ring = ringSel ? data?.rings.find((entry) => entry.id === ringSel) ?? null : null;
  const ringLevel = data?.rings.findIndex((entry) => entry.id === ringSel) ?? -1;

  const tracks = data?.tracks ?? [];
  const sectors = data?.sectors ?? [];
  const names = useMemo(
    () => new Map(planets.map((planet) => [planet.id, planet.title || planet.id])),
    [planets],
  );
  const chosenTrack = tracks.find((entry) => entry.id === track) ?? null;
  const sector = sectorSel ? sectors.find((entry) => entry.id === sectorSel) ?? null : null;
  const hasSun = planets.some((planet) => planet.sun);
  // How the untouched worlds are drawn follows the sky's own warmth.
  const unlit = useMemo(() => unlitFor(planets), [planets]);

  const onPlanet = useCallback((id: string) => {
    setSelClaim(null);
    setSectorSel(null);
    setFocus((was) => (was === id ? null : id));
  }, []);

  const onRing = useCallback((id: string | null) => {
    setFocus(null);
    setSelClaim(null);
    setSectorSel(null);
    setRingSel((was) => (was === id ? null : id));
  }, []);

  const onSector = useCallback((id: string | null) => {
    setFocus(null);
    setSelClaim(null);
    setRingSel(null);
    setSectorSel((was) => (was === id ? null : id));
  }, []);

  const onClaim = useCallback(
    (planetId: string, index: number) => {
      setFocus(planetId);
      setSelClaim((was) => (was === index ? null : index));
    },
    [],
  );

  const onClose = useCallback(() => {
    setFocus(null);
    setSelClaim(null);
  }, []);

  // Opening a claim on a phone puts its shelf below the fold; bring it up.
  useEffect(() => {
    if (selClaim !== null && narrow && shelfRef.current) {
      shelfRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selClaim, narrow]);

  const openInstrument = useCallback(
    (instrument: InstrumentReading) => {
      if (!instrument.ref) return;
      navigate(`/coaching/activity?ref=${encodeURIComponent(instrument.ref)}`);
    },
    [navigate],
  );

  const page: React.CSSProperties = {
    minHeight: "100vh",
    width: "100%",
    background: P.bg,
    color: P.text,
  };
  const column: React.CSSProperties = {
    maxWidth: 620,
    margin: "0 auto",
    padding: narrow ? "14px 12px 56px" : "22px 18px 60px",
  };

  if (status === "loading") {
    return (
      <div style={page}>
        <div style={{ ...column, fontFamily: MONO, fontSize: 13, color: P.dim }} data-testid="atlas-loading">
          charting the sky…
        </div>
      </div>
    );
  }

  if (status === "error" || !data) {
    return (
      <div style={page}>
        <div style={column} data-testid="atlas-error">
          <div style={{ fontFamily: SERIF, fontSize: 18, color: P.text }}>The sky did not load.</div>
          <div style={{ fontFamily: MONO, fontSize: 12, color: P.dim, marginTop: 6 }}>{error}</div>
          <button
            type="button" onClick={reload}
            style={{
              marginTop: 14, borderRadius: 999, padding: "9px 16px", minHeight: 44,
              fontFamily: MONO, fontSize: 13, border: `1px solid ${P.gold}`, color: P.gold,
              background: "transparent", cursor: "pointer",
            }}
          >
            try again
          </button>
        </div>
      </div>
    );
  }

  const hasSky = data.rings.length > 0 || data.planets.length > 0;

  return (
    <div style={page} data-testid="atlas-page">
      <style>{NO_FOCUS_RING}</style>
      <div style={column}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ fontFamily: MONO, fontSize: 12, color: P.faint, letterSpacing: "0.14em" }}>
            YOUR SKY ATLAS
          </div>
          <button
            type="button" onClick={toggle} aria-label={night ? "day sky" : "night sky"}
            data-testid="atlas-sky-toggle"
            style={{
              marginLeft: "auto", borderRadius: 999, padding: "6px 12px", minHeight: 36, minWidth: 42,
              fontFamily: MONO, fontSize: 14, border: `1px solid ${P.line}`, color: P.dim,
              background: "transparent", cursor: "pointer",
            }}
          >
            {night ? "☀" : "☾"}
          </button>
        </div>

        <div
          style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 15, color: P.dim, margin: "6px 0 10px" }}
          data-testid="atlas-sentence"
        >
          {data.sentence}
        </div>

        {hasSky && (
          <div
            style={{ fontFamily: MONO, fontSize: 11.5, color: P.dim, marginBottom: 10 }}
            data-testid="atlas-ledger"
          >
            {[
              `⌂ ${data.ledger.crossed} of ${data.ledger.worlds} worlds crossed`,
              data.ledger.climbing ? `${data.ledger.climbing} climbing` : "",
              data.ledger.granted ? `⊙ ${data.ledger.granted} placed` : "",
              data.ledger.cooling ? `◆ ${data.ledger.cooling} want a look` : "",
              data.ledger.burned ? `⚠ ${data.ledger.burned} to repair` : "",
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
        )}

        {!hasSky && (
          <div style={{ fontFamily: SERIF, fontSize: 15, color: P.dim }} data-testid="atlas-no-taxonomy">
            No rings have been drawn yet. Once an author publishes a taxonomy, your sky appears here.
          </div>
        )}

        {hasSky && (
          <>
            <details
              open={controlsOpen}
              onToggle={(event) => setControlsOpen((event.target as HTMLDetailsElement).open)}
              style={{ marginBottom: 10 }}
              data-testid="atlas-controls"
            >
              <summary
                style={{
                  fontFamily: MONO, fontSize: 12, color: P.faint, letterSpacing: "0.1em",
                  cursor: "pointer", padding: "8px 0", minHeight: 36,
                }}
              >
                MAP SETTINGS
              </summary>

              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                <span style={{ fontFamily: MONO, fontSize: 11, color: P.faint, alignSelf: "center", letterSpacing: "0.1em" }}>
                  GUIDE
                </span>
                {GUIDES.map(([id, label]) => (
                  <Chip key={id} active={guide === id} colour={P.gold} P={P} onClick={() => setGuide(id)}>
                    {label}
                  </Chip>
                ))}
              </div>

              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                <span style={{ fontFamily: MONO, fontSize: 11, color: P.faint, alignSelf: "center", letterSpacing: "0.1em" }}>
                  CREDIT
                </span>
                {CREDIT_STYLES.map(([id, label]) => (
                  <Chip key={id} active={credit === id} colour={P.goldHi} P={P} onClick={() => setCredit(id)}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <CreditSwatch style={id} P={P} />
                      {label}
                    </span>
                  </Chip>
                ))}
                <span
                  style={{ fontFamily: MONO, fontSize: 11, color: P.faint, alignSelf: "center" }}
                >
                  {CREDIT_STYLES.find(([id]) => id === credit)?.[2]}
                </span>
              </div>

              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                <span style={{ fontFamily: MONO, fontSize: 11, color: P.faint, alignSelf: "center", letterSpacing: "0.1em" }}>
                  LINKS
                </span>
                {LINKS.map(([id, label]) => (
                  <Chip key={id} active={links === id} colour={P.dim} P={P} onClick={() => setLinks(id)}>
                    {label}
                  </Chip>
                ))}
              </div>

              {tracks.length > 0 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                  <span style={{ fontFamily: MONO, fontSize: 11, color: P.faint, alignSelf: "center", letterSpacing: "0.1em" }}>
                    TRACK
                  </span>
                  {tracks.map((entry) => (
                    <Chip
                      key={entry.id} active={track === entry.id}
                      colour={trackColour(P, entry.id, hash32)} P={P}
                      onClick={() => setTrack((was) => (was === entry.id ? null : entry.id))}
                    >
                      {entry.label.toLowerCase()}
                    </Chip>
                  ))}
                </div>
              )}
            </details>

            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
              {data.rings.map((entry, index) => (
                <Chip
                  key={entry.id} active={ringSel === entry.id} colour={ringColour(P, index)} P={P}
                  onClick={() => onRing(entry.id)}
                >
                  {entry.name || entry.id}
                </Chip>
              ))}
            </div>
            {sectors.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }} data-testid="atlas-sector-chips">
                {sectors.map((entry) => (
                  <Chip
                    key={entry.id} active={sectorSel === entry.id} colour={sectorColour(P, entry.id, hash32)} P={P}
                    onClick={() => onSector(entry.id)}
                  >
                    {`${entry.symbol} ${entry.label}`}
                  </Chip>
                ))}
              </div>
            )}

            {focused && (
              <nav
                style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}
                aria-label="breadcrumb"
              >
                <button
                  type="button" onClick={onClose} data-testid="atlas-crumb-sky"
                  style={{ fontFamily: MONO, fontSize: 13, color: P.dim, background: "none", border: "none", minHeight: 40, cursor: "pointer", padding: 0 }}
                >
                  the sky
                </button>
                {focused.ringName && (
                  <>
                    <span style={{ color: P.faint }}>▸</span>
                    <button
                      type="button"
                      onClick={() => {
                        setFocus(null);
                        setSelClaim(null);
                        setRingSel(focused.ring);
                      }}
                      style={{
                        fontFamily: MONO, fontSize: 13, background: "none", border: "none",
                        color: ringColour(P, data.rings.findIndex((r) => r.id === focused.ring)),
                        minHeight: 40, cursor: "pointer", padding: 0,
                      }}
                    >
                      {focused.ringName}
                    </button>
                  </>
                )}
                <span style={{ color: P.faint }}>▸</span>
                <span style={{ fontFamily: MONO, fontSize: 13, color: P.text }}>{focused.title || focused.id}</span>
                <button
                  type="button" onClick={onClose} data-testid="atlas-back"
                  style={{
                    marginLeft: "auto", borderRadius: 8, padding: "8px 12px", minHeight: 40,
                    fontFamily: MONO, fontSize: 13, border: `1px solid ${P.line}`, color: P.dim,
                    background: "transparent", cursor: "pointer",
                  }}
                >
                  ‹ back
                </button>
              </nav>
            )}

            {/* On a phone the map takes the column's side gutters too: forty
                names share one field, and every pixel of width is type. */}
            <div style={narrow ? { margin: "0 -12px" } : undefined}>
              <Orrery
                data={data} P={P} focus={focus} ringSel={ringSel} sectorSel={sectorSel}
                selClaim={selClaim} credit={credit} links={links} guide={guide} track={track}
                unlit={unlit} textScale={narrow ? 1.5 : 1.15}
                onPlanet={onPlanet} onRing={onRing} onSector={onSector}
                onClaim={onClaim} onClose={onClose}
              />
            </div>

            {focused && (
              <PlanetCard
                planet={focused} P={P} names={names} tracks={tracks} sectors={data?.sectors}
                selectedClaim={selClaim}
                onOpenRing={() => onRing(focused.ring)} onPlanet={onPlanet}
                onClaim={(index) => onClaim(focused.id, index)}
              />
            )}

            <div ref={shelfRef}>
              {focused && selClaim !== null && focused.claims[selClaim] && (
                <ClaimShelf
                  claim={focused.claims[selClaim]} index={selClaim} total={focused.claims.length}
                  P={P} onOpen={openInstrument}
                />
              )}
            </div>

            {/* the sun is a world, and the sky's own card reads under it */}
            {focused?.sun && <SkyCard data={data} P={P} onPlanet={onPlanet} />}

            {!focused && ring && (
              <RingRoster
                ring={ring} P={P} colour={ringColour(P, ringLevel)} sectors={sectors}
                planets={ring.planets.map((id) => byId.get(id)).filter(Boolean) as typeof planets}
                onPlanet={onPlanet}
              />
            )}

            {!focused && !ring && sector && (
              <SectorCard
                sector={sector} rings={data.rings} P={P} colour={sectorColour(P, sector.id, hash32)}
                planets={sector.planets.map((id) => byId.get(id)).filter(Boolean) as typeof planets}
                onPlanet={onPlanet} onClose={() => onSector(null)}
              />
            )}

            {!focused && !ring && !sector && chosenTrack && (
              <TrackCard
                track={chosenTrack} P={P} onPlanet={onPlanet}
                planets={chosenTrack.planets.map((id) => byId.get(id)).filter(Boolean) as typeof planets}
              />
            )}

            {/* with no sun to open, the sky's card is the resting state */}
            {!focused && !ring && !sector && !chosenTrack && !hasSun && (
              <SkyCard data={data} P={P} onPlanet={onPlanet} />
            )}

            {focused && (
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 10 }}>
                {[-1, 1].map((step) => {
                  const order = data.planets;
                  const index = order.findIndex((planet) => planet.id === focused.id);
                  const neighbour = order[(index + step + order.length) % order.length];
                  if (!neighbour || neighbour.id === focused.id) return null;
                  return (
                    <button
                      key={step}
                      type="button"
                      onClick={() => {
                        setSelClaim(null);
                        setFocus(neighbour.id);
                      }}
                      data-testid={step < 0 ? "atlas-pager-prev" : "atlas-pager-next"}
                      style={{
                        flex: 1, borderRadius: 8, padding: "10px 12px", minHeight: 48,
                        textAlign: step < 0 ? "left" : "right", cursor: "pointer",
                        border: `1px solid ${P.line}`, background: "transparent",
                        fontFamily: MONO, fontSize: 12, color: P.dim,
                      }}
                    >
                      {step < 0 ? "‹ " : ""}
                      {neighbour.title || neighbour.id}
                      {step > 0 ? " ›" : ""}
                    </button>
                  );
                })}
              </div>
            )}

            {!focused && !ring && !sector && data.unplaced.length > 0 && (
              <div style={{ fontFamily: MONO, fontSize: 11, color: P.faint, marginTop: 12 }}>
                {data.unplaced.length} world{data.unplaced.length === 1 ? "" : "s"} drift unplaced —
                no author has said where they belong yet
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AtlasPage;
